import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Sparkles,
  Edit3,
  Trash2,
  Plus,
  Clock,
  Target,
  Play,
  BookOpen,
  Code,
  Eye,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import { aiApi } from '@/utils/api';

interface PreviewCard {
  id: string;
  title: string;
  description?: string;
  type: string;
  estimatedMinutes: number;
  group?: string;
  date?: string;
}

interface TrackPreviewData {
  trackName: string;
  trackColor: string;
  trackIcon: string;
  cards: PreviewCard[];
  totalCards: number;
  totalMinutes: number;
}

interface SchedulePreviewData {
  tasks: PreviewCard[];
  totalTasks: number;
}

interface ActionPreview {
  type: 'create_track' | 'schedule_tasks';
  data: TrackPreviewData | SchedulePreviewData;
}

interface TaskPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionPreview: ActionPreview;
  onSuccess?: () => void;
}

const cardTypeConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  watch: { icon: Play, label: 'Watch', color: 'text-red-500' },
  read: { icon: BookOpen, label: 'Read', color: 'text-blue-500' },
  practice: { icon: Code, label: 'Practice', color: 'text-green-500' },
  build: { icon: Target, label: 'Build', color: 'text-purple-500' },
  revise: { icon: Eye, label: 'Revise', color: 'text-orange-500' },
  notes: { icon: FileText, label: 'Notes', color: 'text-gray-500' },
  custom: { icon: Sparkles, label: 'Custom', color: 'text-pink-500' },
};

const colorOptions = [
  '#8b5cf6', '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6',
  '#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444',
  '#ec4899', '#d946ef', '#a855f7', '#64748b', '#1e293b',
];

const iconOptions = ['🎯', '📚', '💪', '🚀', '💻', '🎨', '🔬', '📊', '🎵', '🌱', '⚡', '🔥', '💡', '🏆', '📝'];

export function TaskPreviewModal({ isOpen, onClose, actionPreview, onSuccess }: TaskPreviewModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  // Editable state
  const [trackName, setTrackName] = useState(
    actionPreview.type === 'create_track' ? (actionPreview.data as TrackPreviewData).trackName : ''
  );
  const [trackColor, setTrackColor] = useState(
    actionPreview.type === 'create_track' ? (actionPreview.data as TrackPreviewData).trackColor : '#8b5cf6'
  );
  const [trackIcon, setTrackIcon] = useState(
    actionPreview.type === 'create_track' ? (actionPreview.data as TrackPreviewData).trackIcon : '🎯'
  );
  const [cards, setCards] = useState<PreviewCard[]>(
    actionPreview.type === 'create_track'
      ? (actionPreview.data as TrackPreviewData).cards
      : (actionPreview.data as SchedulePreviewData).tasks
  );

  const totalMinutes = cards.reduce((sum, c) => sum + c.estimatedMinutes, 0);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  const handleUpdateCard = (id: string, updates: Partial<PreviewCard>) => {
    setCards(cards.map(c => (c.id === id ? { ...c, ...updates } : c)));
    setEditingCard(null);
  };

  const handleDeleteCard = (id: string) => {
    setCards(cards.filter(c => c.id !== id));
  };

  const handleAddCard = () => {
    const newCard: PreviewCard = {
      id: `new-${Date.now()}`,
      title: 'New Task',
      description: '',
      type: 'build',
      estimatedMinutes: 30,
    };
    setCards([...cards, newCard]);
    setEditingCard(newCard.id);
  };

  const handleCreate = async () => {
    if (cards.length === 0) {
      setError('Add at least one task');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      if (actionPreview.type === 'create_track') {
        await aiApi.post('/ai/quick-create', {
          trackName,
          trackColor,
          trackIcon,
          cards: cards.map(c => ({
            title: c.title,
            description: c.description,
            type: c.type,
            estimatedMinutes: c.estimatedMinutes,
          })),
        });
      } else {
        await aiApi.post('/ai/quick-schedule', {
          tasks: cards.map(c => ({
            title: c.title,
            description: c.description,
            type: c.type,
            estimatedMinutes: c.estimatedMinutes,
            date: c.date,
          })),
        });
      }

      setIsSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={cn(
            'relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl',
            'bg-white dark:bg-gray-900 shadow-lg',
            'border border-gray-200 dark:border-gray-800'
          )}
        >
          {/* Success Overlay */}
          <AnimatePresence>
            {isSuccess && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/95 dark:bg-gray-900/95"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center"
                >
                  <CheckCircle className="w-10 h-10 text-white" />
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-4 text-xl font-semibold text-gray-900 dark:text-white"
                >
                  {actionPreview.type === 'create_track' ? 'Track Created!' : 'Tasks Scheduled!'}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-2 text-gray-500 dark:text-gray-400"
                >
                  {cards.length} tasks added successfully
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {actionPreview.type === 'create_track' ? 'Create Track' : 'Schedule Tasks'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Review and customize before creating
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-6 space-y-6">
            {/* Track Settings (for create_track) */}
            {actionPreview.type === 'create_track' && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  {/* Icon Picker */}
                  <div className="relative">
                    <button
                      onClick={() => setShowIconPicker(!showIconPicker)}
                      className={cn(
                        'w-14 h-14 rounded-xl flex items-center justify-center text-2xl',
                        'border-2 border-dashed border-gray-300 dark:border-gray-700',
                        'hover:border-violet-500 dark:hover:border-violet-500 transition-colors'
                      )}
                      style={{ backgroundColor: trackColor + '20' }}
                    >
                      {trackIcon}
                    </button>
                    <AnimatePresence>
                      {showIconPicker && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 mt-2 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-10 grid grid-cols-5 gap-1"
                        >
                          {iconOptions.map(icon => (
                            <button
                              key={icon}
                              onClick={() => {
                                setTrackIcon(icon);
                                setShowIconPicker(false);
                              }}
                              className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              {icon}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Track Name */}
                  <div className="flex-1">
                    <input
                      type="text"
                      value={trackName}
                      onChange={e => setTrackName(e.target.value)}
                      placeholder="Track name"
                      className={cn(
                        'w-full px-4 py-3 rounded-xl text-lg font-medium',
                        'bg-gray-100 dark:bg-gray-800 border-2 border-transparent',
                        'focus:border-violet-500 focus:outline-none transition-colors',
                        'text-gray-900 dark:text-white placeholder-gray-400'
                      )}
                    />
                  </div>

                  {/* Color Picker */}
                  <div className="relative">
                    <button
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      className="p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div
                        className="w-8 h-8 rounded-lg"
                        style={{ backgroundColor: trackColor }}
                      />
                    </button>
                    <AnimatePresence>
                      {showColorPicker && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full right-0 mt-2 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-10 grid grid-cols-5 gap-1"
                        >
                          {colorOptions.map(color => (
                            <button
                              key={color}
                              onClick={() => {
                                setTrackColor(color);
                                setShowColorPicker(false);
                              }}
                              className={cn(
                                'w-8 h-8 rounded-lg transition-transform hover:scale-110',
                                trackColor === color && 'ring-2 ring-offset-2 ring-violet-500'
                              )}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Target className="w-4 h-4" />
                <span className="font-medium">{cards.length} tasks</span>
              </div>
              <div className="w-px h-4 bg-gray-300 dark:bg-gray-700" />
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span className="font-medium">
                  {hours > 0 ? `${hours}h ` : ''}{mins}m total
                </span>
              </div>
            </div>

            {/* Cards List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Tasks</h3>
                <button
                  onClick={handleAddCard}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-violet-600 dark:text-violet-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Task
                </button>
              </div>

              <AnimatePresence mode="popLayout">
                {cards.map((card) => (
                  <motion.div
                    key={card.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.2 }}
                    className={cn(
                      'p-4 rounded-xl border transition-all',
                      editingCard === card.id
                        ? 'border-violet-500 bg-gray-50 dark:bg-gray-800'
                        : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-700'
                    )}
                  >
                    {editingCard === card.id ? (
                      <CardEditor
                        card={card}
                        onSave={(updates) => handleUpdateCard(card.id, updates)}
                        onCancel={() => setEditingCard(null)}
                      />
                    ) : (
                      <div className="flex items-start gap-3">
                        {/* Type Icon */}
                        <div
                          className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                            'bg-gray-100 dark:bg-gray-800'
                          )}
                        >
                          {(() => {
                            const config = cardTypeConfig[card.type] || cardTypeConfig.custom;
                            const Icon = config.icon;
                            return <Icon className={cn('w-5 h-5', config.color)} />;
                          })()}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {card.title}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {card.estimatedMinutes}m
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs">
                              {cardTypeConfig[card.type]?.label || 'Custom'}
                            </span>
                            {card.group && (
                              <span className="text-xs text-gray-400">{card.group}</span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingCard(card.id)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <Edit3 className="w-4 h-4 text-gray-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteCard(card.id)}
                            className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              {cards.length === 0 && (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <Target className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="mt-4 text-gray-500 dark:text-gray-400">No tasks yet</p>
                  <button
                    onClick={handleAddCard}
                    className="mt-2 text-violet-600 dark:text-violet-400 font-medium hover:text-violet-700 dark:hover:text-violet-300"
                  >
                    Add your first task
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            <div className="flex-1" />
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={isCreating || cards.length === 0}
                className={cn(
                  'flex items-center gap-2 px-5 py-2 rounded-full font-medium',
                  'bg-violet-600 hover:bg-violet-700 text-white',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-all'
                )}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Create {actionPreview.type === 'create_track' ? 'Track' : 'Tasks'}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

// Card Editor Sub-component
function CardEditor({
  card,
  onSave,
  onCancel,
}: {
  card: PreviewCard;
  onSave: (updates: Partial<PreviewCard>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description || '');
  const [type, setType] = useState(card.type);
  const [minutes, setMinutes] = useState(card.estimatedMinutes);

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Task title"
        autoFocus
        className={cn(
          'w-full px-3 py-2 rounded-lg text-sm',
          'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
          'focus:border-violet-500 focus:outline-none',
          'text-gray-900 dark:text-white'
        )}
      />
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className={cn(
          'w-full px-3 py-2 rounded-lg text-sm resize-none',
          'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
          'focus:border-violet-500 focus:outline-none',
          'text-gray-900 dark:text-white'
        )}
      />
      <div className="flex items-center gap-3">
        <select
          value={type}
          onChange={e => setType(e.target.value)}
          className={cn(
            'px-3 py-2 rounded-lg text-sm',
            'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
            'focus:border-violet-500 focus:outline-none',
            'text-gray-900 dark:text-white'
          )}
        >
          {Object.entries(cardTypeConfig).map(([key, config]) => (
            <option key={key} value={key}>
              {config.label}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <input
            type="number"
            value={minutes}
            onChange={e => setMinutes(Number(e.target.value))}
            min={5}
            max={480}
            step={5}
            className={cn(
              'w-20 px-3 py-2 rounded-lg text-sm',
              'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
              'focus:border-violet-500 focus:outline-none',
              'text-gray-900 dark:text-white'
            )}
          />
          <span className="text-sm text-gray-500">min</span>
        </div>
        <div className="flex-1" />
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave({ title, description, type, estimatedMinutes: minutes })}
          className="px-3 py-1.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-full transition-colors"
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default TaskPreviewModal;
